import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { StatCard } from '@/components/AdminShared';
import { Clapperboard, Film, Coins, Save, Loader2, AlertTriangle, Gauge } from 'lucide-react';
import { toast } from 'sonner';

const NumberField = ({ id, label, value, onChange, step = '1', min = '0' }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
    <Input id={id} data-testid={id} type="number" step={step} min={min} value={value}
      onChange={(e) => onChange(e.target.value)} className="bg-[#0A0A0A] border-white/10" />
  </div>
);

export const AdminKaiaVideo = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/kaia-video/settings');
      setData(res.data);
      const c = res.data.config;
      setForm({
        enabled: c.enabled,
        quota_free: String(c.quota.free),
        quota_pro: String(c.quota.pro),
        quota_elite: String(c.quota.elite),
        credit_cost: String(c.credit_cost),
        monthly_video_cap: String(c.monthly_video_cap),
        monthly_budget_eur: String(c.monthly_budget_eur),
        est_cost_per_video_eur: String(c.est_cost_per_video_eur),
      });
    } catch (e) {
      toast.error(apiError(e, t('adminKaia.saveError')));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        quota_free: parseInt(form.quota_free, 10) || 0,
        quota_pro: parseInt(form.quota_pro, 10) || 0,
        quota_elite: parseInt(form.quota_elite, 10) || 0,
        credit_cost: parseInt(form.credit_cost, 10) || 0,
        monthly_video_cap: parseInt(form.monthly_video_cap, 10) || 0,
        monthly_budget_eur: parseFloat(form.monthly_budget_eur) || 0,
        est_cost_per_video_eur: parseFloat(form.est_cost_per_video_eur) || 0,
      };
      await api.put('/admin/kaia-video/settings', payload);
      toast.success(t('adminKaia.saved'));
      await load();
    } catch (e) {
      toast.error(apiError(e, t('adminKaia.saveError')));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-kaia-loading">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
    );
  }

  const usage = data.usage || {};
  const videosPct = Math.min(100, usage.videos_pct ?? 0);
  const budgetPct = Math.min(100, usage.budget_pct ?? 0);
  const near = !usage.cap_reached && (videosPct >= 80 || budgetPct >= 80);

  return (
    <div className="space-y-6" data-testid="admin-kaia-video">
      <div>
        <h3 className="font-display text-xl flex items-center gap-2"><Clapperboard className="w-5 h-5 text-[#00B4D8]" /> {t('adminKaia.title')}</h3>
        <p className="text-sm text-muted-foreground mt-0.5">{t('adminKaia.subtitle')}</p>
      </div>

      {/* Observability */}
      {usage.cap_reached ? (
        <div className="flex items-start gap-2 rounded-xl border border-[#FF5C5C]/30 bg-[#FF5C5C]/8 px-4 py-3" data-testid="admin-kaia-cap-reached">
          <AlertTriangle className="w-4 h-4 text-[#FF5C5C] mt-0.5 shrink-0" />
          <p className="text-xs text-white/85">{t('adminKaia.capReached')}</p>
        </div>
      ) : near ? (
        <div className="flex items-start gap-2 rounded-xl border border-[#FFB020]/30 bg-[#FFB020]/8 px-4 py-3" data-testid="admin-kaia-cap-warning">
          <AlertTriangle className="w-4 h-4 text-[#FFB020] mt-0.5 shrink-0" />
          <p className="text-xs text-white/85">{t('adminKaia.capWarning')}</p>
        </div>
      ) : null}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Film} label={t('adminKaia.videosThisMonth')} value={usage.videos_this_month ?? 0} accent="#00B4D8" testid="admin-kaia-stat-videos" />
        <StatCard icon={Coins} label={t('adminKaia.estimatedCost')} value={`€${(usage.estimated_cost_eur ?? 0).toFixed(2)}`} accent="#2EE59D" testid="admin-kaia-stat-cost" />
        <StatCard icon={Gauge} label={t('adminKaia.period')} value={usage.period ?? '—'} accent="#48CAE4" testid="admin-kaia-stat-period" />
      </div>

      <div className="kite-card p-5 space-y-4">
        <h4 className="label-eyebrow text-muted-foreground">{t('adminKaia.usageTitle')}</h4>
        <div>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-white/85">{t('adminKaia.capUsage')}</span>
            <span className="font-data text-muted-foreground">{usage.videos_this_month ?? 0} / {usage.cap ?? 0}</span>
          </div>
          <Progress value={videosPct} className="h-2" data-testid="admin-kaia-cap-bar" />
        </div>
        <div>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-white/85">{t('adminKaia.budgetUsage')}</span>
            <span className="font-data text-muted-foreground">€{(usage.estimated_cost_eur ?? 0).toFixed(2)} / €{(usage.budget_eur ?? 0).toFixed(2)}</span>
          </div>
          <Progress value={budgetPct} className="h-2" data-testid="admin-kaia-budget-bar" />
        </div>
      </div>

      {/* Settings */}
      <div className="kite-card p-5 space-y-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-medium">{t('adminKaia.enabled')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('adminKaia.enabledHint')}</p>
          </div>
          <Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="admin-kaia-enabled-switch" />
        </div>

        <div className="border-t border-white/10 pt-4">
          <h4 className="label-eyebrow text-muted-foreground mb-3">{t('adminKaia.quotaTitle')}</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <NumberField id="admin-kaia-quota-free" label={t('adminKaia.quotaFree')} value={form.quota_free} onChange={(v) => set('quota_free', v)} />
            <NumberField id="admin-kaia-quota-pro" label={t('adminKaia.quotaPro')} value={form.quota_pro} onChange={(v) => set('quota_pro', v)} />
            <NumberField id="admin-kaia-quota-elite" label={t('adminKaia.quotaElite')} value={form.quota_elite} onChange={(v) => set('quota_elite', v)} />
            <NumberField id="admin-kaia-credit-cost" label={t('adminKaia.creditCost')} value={form.credit_cost} onChange={(v) => set('credit_cost', v)} />
          </div>
        </div>

        <div className="border-t border-white/10 pt-4">
          <h4 className="label-eyebrow text-muted-foreground mb-3">{t('adminKaia.capsTitle')}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <NumberField id="admin-kaia-cap" label={t('adminKaia.monthlyCap')} value={form.monthly_video_cap} onChange={(v) => set('monthly_video_cap', v)} />
            <NumberField id="admin-kaia-budget" label={t('adminKaia.monthlyBudget')} value={form.monthly_budget_eur} onChange={(v) => set('monthly_budget_eur', v)} step="0.01" />
            <NumberField id="admin-kaia-estcost" label={t('adminKaia.estCost')} value={form.est_cost_per_video_eur} onChange={(v) => set('est_cost_per_video_eur', v)} step="0.01" />
          </div>
        </div>

        <Button onClick={save} disabled={saving} data-testid="admin-kaia-save"
          className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} {t('adminKaia.save')}
        </Button>
      </div>
    </div>
  );
};
